import { useEffect, useState, useRef } from 'react';

/**
 * AIBrainHalo
 * 
 * A stylized neural ring / brain halo SVG that wraps around the hero copy group
 * (heading + subtitle together).
 * 
 * Features:
 * - Elliptical ring with neural nodes
 * - Synapse connection lines
 * - Subtle animations (rotation, node pulses)
 * - Respects prefers-reduced-motion
 * - Positioned absolutely to wrap around parent content
 */
const AIBrainHalo = ({ className = '' }) => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const containerRef = useRef(null);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = (e) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Intersection Observer for visibility
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => setIsVisible(entries[0].isIntersecting),
      { threshold: 0 }
    );
    
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }
    
    return () => observer.disconnect();
  }, []);

  // Neural node positions around the ellipse (in degrees)
  const nodePositions = [
    { angle: 0, size: 2.5, delay: 0 },
    { angle: 25, size: 2, delay: 1.2 },
    { angle: 55, size: 1.8, delay: 2.5 },
    { angle: 85, size: 2.5, delay: 0.8 },
    { angle: 110, size: 2, delay: 3.2 },
    { angle: 140, size: 2.2, delay: 1.8 },
    { angle: 165, size: 2, delay: 4 },
    { angle: 195, size: 2.5, delay: 0.5 },
    { angle: 220, size: 1.8, delay: 2.8 },
    { angle: 250, size: 2.2, delay: 1.5 },
    { angle: 280, size: 2.5, delay: 3.5 },
    { angle: 310, size: 2, delay: 0.3 },
    { angle: 340, size: 1.8, delay: 2 },
  ];

  // Calculate position on ellipse
  const getEllipsePoint = (angleDeg, rx, ry, cx, cy) => {
    const rad = (angleDeg * Math.PI) / 180;
    return {
      x: cx + rx * Math.cos(rad),
      y: cy + ry * Math.sin(rad),
    };
  };

  // SVG dimensions - wider ellipse to wrap around text block
  const width = 800;
  const height = 320;
  const cx = width / 2;
  const cy = height / 2;
  const rx = 370; // Horizontal radius - wide to wrap text
  const ry = 130; // Vertical radius - taller to include subtitle
  const innerRx = rx - 35;
  const innerRy = ry - 18;

  // Generate synapse paths (curved connections inside the ellipse)
  const synapsePaths = [
    { from: 30, to: 150, curve: -25 },
    { from: 210, to: 330, curve: 25 },
    { from: 75, to: 285, curve: -15 },
    { from: 120, to: 240, curve: 20 },
  ];

  const shouldAnimate = !prefersReducedMotion && isVisible;

  return (
    <div 
      ref={containerRef}
      className={`absolute inset-0 flex items-center justify-center pointer-events-none ${className}`}
      style={{ 
        zIndex: 1,
        // Extend beyond the text container to create padding
        top: '-30px',
        bottom: '-30px',
        left: '-40px',
        right: '-40px',
      }}
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="w-full h-full"
        style={{ 
          maxWidth: '800px',
          opacity: 0.18,
          filter: 'drop-shadow(0 0 12px rgba(59, 130, 246, 0.25))',
        }}
        preserveAspectRatio="xMidYMid meet"
        data-testid="brain-halo-svg"
      >
        <defs>
          {/* Gradient for the main ring */}
          <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.9" />
            <stop offset="35%" stopColor="#00E5FF" stopOpacity="0.7" />
            <stop offset="65%" stopColor="#3B82F6" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.9" />
          </linearGradient>
          
          {/* Gradient for inner elements */}
          <linearGradient id="synapseGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.4" />
            <stop offset="50%" stopColor="#3B82F6" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#00E5FF" stopOpacity="0.4" />
          </linearGradient>
          
          {/* Soft glow filter */}
          <filter id="softGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.5" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          
          {/* Stronger pulse glow */}
          <filter id="pulseGlow" x="-100%" y="-100%" width="300%" height="300%">
            <feGaussianBlur stdDeviation="2.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        
        {/* Outer rotating ring group (very slow) */}
        <g 
          style={{
            transformOrigin: `${cx}px ${cy}px`,
            animation: shouldAnimate ? 'haloRotateSlow 45s linear infinite' : 'none',
          }}
        >
          {/* Main ellipse ring - organic dashed pattern */}
          <ellipse
            cx={cx}
            cy={cy}
            rx={rx}
            ry={ry}
            fill="none"
            stroke="url(#ringGradient)"
            strokeWidth="1.2"
            strokeDasharray="12 6 4 6"
            opacity="0.7"
          />
          
          {/* Secondary inner ring - thinner */}
          <ellipse
            cx={cx}
            cy={cy}
            rx={innerRx}
            ry={innerRy}
            fill="none"
            stroke="url(#ringGradient)"
            strokeWidth="0.6"
            strokeDasharray="6 10"
            opacity="0.35"
          />
          
          {/* Tertiary subtle outer ring */}
          <ellipse
            cx={cx}
            cy={cy}
            rx={rx + 15}
            ry={ry + 8}
            fill="none"
            stroke="url(#ringGradient)"
            strokeWidth="0.4"
            strokeDasharray="3 12"
            opacity="0.2"
          />
        </g>
        
        {/* Synapse connections inside the ellipse */}
        <g opacity="0.5">
          {synapsePaths.map((synapse, i) => {
            const from = getEllipsePoint(synapse.from, innerRx * 0.65, innerRy * 0.65, cx, cy);
            const to = getEllipsePoint(synapse.to, innerRx * 0.65, innerRy * 0.65, cx, cy);
            const midX = (from.x + to.x) / 2;
            const midY = (from.y + to.y) / 2 + synapse.curve;
            
            return (
              <path
                key={i}
                d={`M ${from.x} ${from.y} Q ${midX} ${midY} ${to.x} ${to.y}`}
                fill="none"
                stroke="url(#synapseGradient)"
                strokeWidth="0.6"
                strokeDasharray={shouldAnimate ? "5 5" : "none"}
                style={{
                  animation: shouldAnimate 
                    ? `synapseDash 10s linear infinite ${i * 2.5}s` 
                    : 'none',
                }}
              />
            );
          })}
        </g>
        
        {/* Neural nodes around the ring */}
        <g filter="url(#softGlow)">
          {nodePositions.map((node, i) => {
            const pos = getEllipsePoint(node.angle, rx, ry, cx, cy);
            
            return (
              <g key={i}>
                {/* Node outer glow - pulses */}
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r={node.size * 3}
                  fill="#3B82F6"
                  opacity="0.12"
                  style={{
                    animation: shouldAnimate 
                      ? `nodePulse 5s ease-in-out infinite ${node.delay}s` 
                      : 'none',
                  }}
                />
                
                {/* Node middle */}
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r={node.size * 1.5}
                  fill="#00E5FF"
                  opacity="0.5"
                  style={{
                    animation: shouldAnimate 
                      ? `nodePulse 5s ease-in-out infinite ${node.delay}s` 
                      : 'none',
                  }}
                />
                
                {/* Node core */}
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r={node.size * 0.5}
                  fill="#FFFFFF"
                  opacity="0.85"
                />
              </g>
            );
          })}
        </g>
        
        {/* Signal pulse traveling along the outer ring */}
        {shouldAnimate && (
          <g>
            {/* Primary signal - travels clockwise */}
            <circle r="3.5" fill="#00E5FF" filter="url(#pulseGlow)">
              <animateMotion
                dur="14s"
                repeatCount="indefinite"
                path={`M ${cx + rx} ${cy} A ${rx} ${ry} 0 1 1 ${cx - rx} ${cy} A ${rx} ${ry} 0 1 1 ${cx + rx} ${cy}`}
              />
              <animate
                attributeName="opacity"
                values="0.7;1;0.7"
                dur="1.2s"
                repeatCount="indefinite"
              />
            </circle>
            
            {/* Secondary signal - travels counter-clockwise, offset */}
            <circle r="2.5" fill="#8B5CF6" filter="url(#pulseGlow)">
              <animateMotion
                dur="18s"
                repeatCount="indefinite"
                begin="7s"
                path={`M ${cx - rx} ${cy} A ${rx} ${ry} 0 1 0 ${cx + rx} ${cy} A ${rx} ${ry} 0 1 0 ${cx - rx} ${cy}`}
              />
              <animate
                attributeName="opacity"
                values="0.5;0.85;0.5"
                dur="1.5s"
                repeatCount="indefinite"
              />
            </circle>
          </g>
        )}
        
        {/* Corner accent nodes for neural network feel */}
        <g opacity="0.25">
          {[
            { x: cx - rx * 0.75, y: cy - ry * 0.5, targetAngle: 150 },
            { x: cx + rx * 0.75, y: cy - ry * 0.5, targetAngle: 30 },
            { x: cx - rx * 0.75, y: cy + ry * 0.5, targetAngle: 210 },
            { x: cx + rx * 0.75, y: cy + ry * 0.5, targetAngle: 330 },
          ].map((pos, i) => {
            const ringPoint = getEllipsePoint(pos.targetAngle, rx * 0.92, ry * 0.92, cx, cy);
            return (
              <g key={i}>
                <circle
                  cx={pos.x}
                  cy={pos.y}
                  r="1.5"
                  fill="#3B82F6"
                  style={{
                    animation: shouldAnimate 
                      ? `nodePulse 6s ease-in-out infinite ${i * 1.5}s` 
                      : 'none',
                  }}
                />
                {/* Connection line to ring */}
                <line
                  x1={pos.x}
                  y1={pos.y}
                  x2={ringPoint.x}
                  y2={ringPoint.y}
                  stroke="#3B82F6"
                  strokeWidth="0.4"
                  strokeDasharray="2 4"
                  opacity="0.4"
                />
              </g>
            );
          })}
        </g>
      </svg>
      
      {/* CSS Keyframes */}
      <style>{`
        @keyframes haloRotateSlow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes nodePulse {
          0%, 100% { 
            opacity: 0.4; 
            transform: scale(1); 
          }
          50% { 
            opacity: 0.9; 
            transform: scale(1.15); 
          }
        }
        
        @keyframes synapseDash {
          from { stroke-dashoffset: 0; }
          to { stroke-dashoffset: 20; }
        }
      `}</style>
    </div>
  );
};

export default AIBrainHalo;
